import { FC, RefObject, useCallback, useMemo, useState } from 'react';
import { ScenarioModalContent } from './ScenarioModalContent';
import { IressCol, IressModal, IressStack, IressText } from '@iress-oss/ids-components';
import { Nullable, SkeletonElement } from '@xplan-pension-switching/common';
import { useRetirementFundsQuery } from '../../../services';
import { PageErrorWithReload } from '../../PageErrorWithReload/PageErrorWithReload';
import { ScenarioModalFooter } from './ScenarioModalFooter';
import { LoadingFields } from '../../LoadingFields/LoadingFields';

export interface ScenarioModalProps {
    onSubmit: (selectedFunds: number[]) => Promise<{ success: boolean; errorMessage: Nullable<string> }>;
    selectedEntityId?: number;
    container?: RefObject<Nullable<HTMLDivElement>>;
}

const loadingPlaceholder =
    <IressStack gutter="lg">
        <IressCol span={{md: 12}}>
            <IressText element="h2">Select funds for pension switching scenario</IressText>
        </IressCol>
        <IressCol span={{md: 12}}>
            <LoadingFields fields={[SkeletonElement.Text]} testId='placeholder-modal-info' />
        </IressCol>
        <IressCol span={{md: 12}}>
            <LoadingFields fields={[SkeletonElement.Text]} testId='placeholder-modal-table' />
        </IressCol>
    </IressStack>;

const useScenarioModalData = ({ onSubmit, selectedEntityId }: ScenarioModalProps) => {
    const { data, isLoading, isError } = useRetirementFundsQuery(selectedEntityId);
    const hasFunds = useMemo(() => !!(data && data.retirementFunds.length > 0), [data]);
    const initialModalFunds = useMemo(
        () => data?.retirementFunds.map(item => ({ ...item, isSelected: false })) ?? [],
        [data]
    );
    const [selectedRetirementFunds, setSelectedRetirementFunds] = useState<number[]>([]);
    const [noFundsError, setNoFundsError] = useState(false);

    const onSelectionChanged = useCallback((retirementFundIds: number[]) => {
        setSelectedRetirementFunds(Array.from(new Set([...retirementFundIds])));
    }, []);

    const handleModalSubmit = useCallback(async () => {
        if (selectedRetirementFunds.length === 0) {
            setNoFundsError(true);
            return { success: false, isLoading: false };
        }
        return await onSubmit(selectedRetirementFunds);
    }, [onSubmit, selectedRetirementFunds]);

    return {
        isLoading,
        isError,
        hasFunds,
        initialModalFunds,
        selectedRetirementFunds,
        onSelectionChanged,
        handleModalSubmit,
        noFundsError,
        setNoFundsError,
    };
};

export const ScenarioModal: FC<ScenarioModalProps> = ({ onSubmit, container, selectedEntityId }) => {
    const {
        isLoading,
        hasFunds,
        handleModalSubmit,
        isError,
        initialModalFunds,
        onSelectionChanged,
        noFundsError,
        setNoFundsError,
    } = useScenarioModalData({ onSubmit, selectedEntityId });

    return (
        <IressModal
            size={'lg'}
            id="scenario-modal"
            data-testid={`scenario-modal`}
            footer={<ScenarioModalFooter hasFunds={hasFunds} onSubmit={handleModalSubmit} isLoading={isLoading} />}
            container={container}
            onExited={() => setNoFundsError(false)}
        >
            <IressStack>
                <IressText element="h2">
                    {!isLoading && hasFunds && 'Select funds for pension switching scenario'}
                    {!isLoading && !hasFunds && 'No available pension plans'}
                </IressText>
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {isLoading && !isError && loadingPlaceholder}
                    {isError && <PageErrorWithReload />}
                    {!isLoading && !isError && (
                        <ScenarioModalContent
                            retirementFunds={initialModalFunds}
                            hasFunds={hasFunds}
                            onSelectionChanged={onSelectionChanged}
                            noFundsError={noFundsError}
                        />
                    )}
                </div>
            </IressStack>
        </IressModal>
    );
};
